import {
  doesFocusableExist,
  getCurrentFocusKey,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import { cancelSmoothScroll } from "../../utils/animateScroll";

var rtlScrollType = null;

export function detectRtlScrollType() {
  var scroller;
  var content;

  if (rtlScrollType) return rtlScrollType;
  if (
    typeof document === "undefined" ||
    !document.createElement ||
    !document.body
  ) {
    return "negative";
  }

  scroller = document.createElement("div");
  content = document.createElement("div");
  scroller.dir = "rtl";
  scroller.style.width = "4px";
  scroller.style.height = "1px";
  scroller.style.position = "absolute";
  scroller.style.top = "-1000px";
  scroller.style.overflow = "scroll";
  content.style.width = "8px";
  content.style.height = "1px";
  scroller.appendChild(content);
  document.body.appendChild(scroller);

  if (scroller.scrollLeft > 0) {
    rtlScrollType = "default";
  } else {
    scroller.scrollLeft = 1;
    rtlScrollType = scroller.scrollLeft === 0 ? "negative" : "reverse";
  }

  document.body.removeChild(scroller);
  return rtlScrollType;
}

export function getRtlScrollStartOffset(node, scrollType) {
  var maxScrollLeft;

  if (!node) return 0;
  maxScrollLeft = Math.max(
    0,
    (node.scrollWidth || 0) - (node.clientWidth || 0)
  );

  return scrollType === "default" ? maxScrollLeft : 0;
}

export function setRtlScrollStart(node, scrollType) {
  if (!node) return;
  node.scrollLeft = getRtlScrollStartOffset(
    node,
    scrollType || detectRtlScrollType()
  );
}

function getTranslateX(node) {
  var transform = node && node.style ? node.style.transform : "";
  var match;

  if (!transform || transform === "none") return 0;

  match = /translateX\((-?[0-9.]+)px\)/.exec(transform);
  if (match) return parseFloat(match[1]) || 0;

  match = /matrix\(([^)]+)\)/.exec(transform);
  if (match) {
    var parts = match[1].split(",");
    return parseFloat(parts[4]) || 0;
  }

  return 0;
}

function getFocusedNode() {
  if (typeof document === "undefined" || !document.querySelector) return null;
  return document.querySelector('[data-spatial-focused="true"]');
}

function getMaxTranslateX(innerNode, scrollerRect, leftPadding, currentX) {
  var children = innerNode ? innerNode.children : null;
  var minLeft = Infinity;
  var i;

  if (!children || !children.length) return 0;

  for (i = 0; i < children.length; i += 1) {
    if (!children[i].getBoundingClientRect) continue;
    var rect = children[i].getBoundingClientRect();
    minLeft = Math.min(minLeft, rect.left - currentX);
  }

  if (minLeft === Infinity) return 0;
  return Math.max(0, scrollerRect.left + leftPadding - minLeft);
}

function setTransformTransition(node, value) {
  if (!node || !node.style) return;
  node.style.transition = value;
  node.style.webkitTransition = value;
  node.style.OTransition = value;
}

export function syncFocusedRowTransform(options) {
  var config = options || {};
  var innerNode = config.innerRef && config.innerRef.current;
  var scrollerNode = config.scrollerRef && config.scrollerRef.current;
  var focusedNode = getFocusedNode();
  var leftPadding = config.leftPadding == null ? 300 : config.leftPadding;
  var rightPadding = config.rightPadding == null ? 48 : config.rightPadding;
  var nextX;
  var maxX;
  var desiredForFocused = 0;

  if (!innerNode) return;

  if (!focusedNode || !innerNode.contains(focusedNode)) {
    innerNode.style.transform = "translateX(0px)";
    return;
  }

  if (!focusedNode.getBoundingClientRect) return;
  if (!scrollerNode || !scrollerNode.getBoundingClientRect) {
    scrollerNode = innerNode.parentNode;
  }
  if (!scrollerNode || !scrollerNode.getBoundingClientRect) return;

  var currentX = getTranslateX(innerNode);
  var focusedRect = focusedNode.getBoundingClientRect();
  var scrollerRect = scrollerNode.getBoundingClientRect();
  var minVisibleLeft = scrollerRect.left + leftPadding;
  var maxVisibleRight = scrollerRect.right - rightPadding;

  nextX = currentX;
  if (focusedRect.left < minVisibleLeft) {
    desiredForFocused = currentX + (minVisibleLeft - focusedRect.left);
    nextX = desiredForFocused;
  } else if (focusedRect.right > maxVisibleRight) {
    nextX = currentX - (focusedRect.right - maxVisibleRight);
  }

  maxX = Math.max(
    getMaxTranslateX(innerNode, scrollerRect, leftPadding, currentX),
    desiredForFocused
  );
  nextX = Math.max(0, Math.min(nextX, maxX));

  innerNode.style.transform = "translateX(" + Math.round(nextX * 100) / 100 + "px)";
}

export function resetRowTransform(innerRef) {
  var innerNode = innerRef && innerRef.current;
  var parentNode = innerNode && innerNode.parentNode;
  var previousTransition;
  var previousWebkitTransition;
  var previousOTransition;

  if (innerNode && innerNode.style) {
    previousTransition = innerNode.style.transition || "";
    previousWebkitTransition = innerNode.style.webkitTransition || "";
    previousOTransition = innerNode.style.OTransition || "";

    setTransformTransition(innerNode, "none");
    innerNode.style.transform = "translateX(0px)";

    if (parentNode) {
      setRtlScrollStart(parentNode);
    }

    setTimeout(function () {
      if (!innerNode || !innerNode.style) return;
      innerNode.style.transition = previousTransition;
      innerNode.style.webkitTransition = previousWebkitTransition;
      innerNode.style.OTransition = previousOTransition;
    }, 0);
  }
}

export function resetRowTransformIfUnfocused(innerRef) {
  var innerNode = innerRef && innerRef.current;
  var focusedNode = getFocusedNode();

  if (!innerNode) return;
  if (!focusedNode || !innerNode.contains(focusedNode)) {
    resetRowTransform(innerRef);
  }
}

export function resetRowTransformAfterBlur(innerRef) {
  setTimeout(function () {
    resetRowTransformIfUnfocused(innerRef);
  }, 0);
}

export function resetHomeRowPositions(rootNode) {
  if (!rootNode || !rootNode.querySelectorAll) return;

  var transformNodes = rootNode.querySelectorAll(
    ".legacy-showcase-row-content, .legacy-turkylar-content, .legacy-static-slider-content, .legacy-penta-content"
  );
  var scrollerNodes = rootNode.querySelectorAll(
    ".legacy-showcase-row-scroller, .legacy-turkylar-scroller, .legacy-static-slider-scroller, .legacy-penta-scroller"
  );
  var i;

  for (i = 0; i < transformNodes.length; i += 1) {
    if (transformNodes[i] && transformNodes[i].style) {
      transformNodes[i].style.transform = "translateX(0px)";
    }
  }

  for (i = 0; i < scrollerNodes.length; i += 1) {
    if (scrollerNodes[i]) {
      setRtlScrollStart(scrollerNodes[i]);
    }
  }
}

export function resetDocumentScroll() {
  if (typeof document !== "undefined") {
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    }
  }
}

export function resetHomeViewport(rootNode) {
  resetDocumentScroll();

  if (rootNode) {
    cancelSmoothScroll(rootNode);
    rootNode.scrollTop = 0;
    rootNode.scrollLeft = 0;
    resetHomeRowPositions(rootNode);
  }
}

export function focusHomeTopSlider() {
  var topSliderNode;
  var topSliderFocusKey;

  if (typeof document === "undefined" || !document.querySelector) return true;

  topSliderNode = document.querySelector("[data-home-top-slider-focus-key]");
  topSliderFocusKey =
    topSliderNode && topSliderNode.getAttribute
      ? topSliderNode.getAttribute("data-home-top-slider-focus-key")
      : null;

  if (topSliderFocusKey && doesFocusableExist(topSliderFocusKey)) {
    setFocus(topSliderFocusKey, { skipScroll: true });
    resetHomeViewport(document.querySelector(".legacy-home-route-scroll"));
    return false;
  }

  return true;
}

export function focusHomeMenu() {
  var currentFocusKey;
  var pathname =
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "/";
  var targetFocusKey = "menu_home";

  if (pathname.indexOf("/search") === 0) {
    targetFocusKey = "menu_search";
  } else if (
    pathname.indexOf("/categories") === 0 ||
    pathname.indexOf("/category/") === 0 ||
    pathname.indexOf("/genre/") === 0
  ) {
    targetFocusKey = "menu_categories";
  } else if (
    pathname.indexOf("/movie") === 0 ||
    pathname.indexOf("/animation/") === 0
  ) {
    targetFocusKey = "menu_movie";
  } else if (pathname.indexOf("/series") === 0) {
    targetFocusKey = "menu_series";
  } else if (pathname.indexOf("/music") === 0) {
    targetFocusKey = "menu_music";
  } else if (pathname.indexOf("/anavatan") === 0) {
    targetFocusKey = "menu_anavatan";
  } else if (pathname.indexOf("/moftabakh") === 0) {
    targetFocusKey = "menu_moftabakh";
  } else if (pathname.indexOf("/profile") === 0) {
    targetFocusKey = "menu_profile";
  } else if (pathname.indexOf("/login") === 0) {
    targetFocusKey = "menu_login";
  }

  if (doesFocusableExist(targetFocusKey)) {
    setFocus(targetFocusKey);
    currentFocusKey = getCurrentFocusKey();
    if (currentFocusKey === targetFocusKey) {
      return false;
    }
  }

  if (doesFocusableExist("MENU")) {
    setFocus("MENU");
    currentFocusKey = getCurrentFocusKey();
    if (currentFocusKey && currentFocusKey.indexOf("menu_") === 0) {
      return false;
    }
  }

  setTimeout(function () {
    if (getCurrentFocusKey() && getCurrentFocusKey().indexOf("menu_") === 0) return;
    if (doesFocusableExist("MENU")) {
      setFocus("MENU");
      return;
    }
    if (doesFocusableExist(targetFocusKey)) {
      setFocus(targetFocusKey);
      return;
    }
    if (doesFocusableExist("menu_home")) {
      setFocus("menu_home");
    }
  }, 0);

  if (
    doesFocusableExist(targetFocusKey) ||
    doesFocusableExist("menu_home") ||
    doesFocusableExist("MENU")
  ) {
    return false;
  }

  return true;
}
